import { useState, useMemo, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getFieldsForProcedureType } from '../config/procedureTypeFields';
import { formatNotesForDisplay, formatStructuredDataForDisplay, isNotesJson } from '../utils/formatters';
import type { PendingVersionSummary } from '../api/types';
import { PendingDot } from './PendingChangeIndicator';
import { PendingChangeToggle, PendingChangeDiff, applyPendingChanges } from './PendingChangeToggle';
import { PendingChangeReview } from './PendingChangeReview';

// Human-readable display names for database fields
const FIELD_DISPLAY_NAMES: Record<string, string> = {
  title: 'Title',
  procedure_type: 'Procedure Type',
  status_sample: 'Sample Status',
  status_patient: 'Patient Status',
  sample_type: 'Sample Type',
  sample_subtype: 'Sample Subtype',
  event_start_date: 'Start Date',
  event_end_date: 'End Date',
  next_check_in_date: 'Next Check-in',
  performed_on: 'Performed On',
  outcome_notes: 'Notes',
  priority: 'Priority',
  blocking: 'Blocking',
  transfer_involved: 'Transfer Involved',
  sample_received: 'Sample Received',
  analysis_complete: 'Analysis Complete',
  is_active: 'Active',
  derived_material: 'Derived Material',
  place: 'Place',
  transfer_method: 'Transfer Method',
  // FK fields - these will show the ID but ideally should be resolved
  subject_id: 'Subject',
  banking_entity_id: 'Banking Entity',
  performing_entity_id: 'Performing Entity',
  location_performed_id: 'Location Performed',
  location_destination_id: 'Location Destination',
  transfer_origin_id: 'Transfer Origin',
  transfer_destination_id: 'Transfer Destination',
  source_sample_id: 'Source Sample',
  blocked_by_sample_id: 'Blocked By',
};

interface TimelineViewProps {
  rows: any[];
  columns: Array<{ columnName: string; dataType: string; isNullable: boolean }>;
  onEditClick?: (row: any) => void;
  onDeleteClick?: (row: any) => void;
  onCreateClick?: (prefillDate?: Date) => void;
  onHistoryClick?: (row: any) => void;
  pendingChanges?: Record<string, PendingVersionSummary>;
}

interface TimelineEvent {
  row: any;
  date: Date;
  endDate: Date | null;
  hasRealDate: boolean;
}

interface DayGroup {
  date: Date;
  events: TimelineEvent[];
}

interface MonthGroup {
  year: number;
  month: number;
  days: DayGroup[];
}

interface FlatDayColumn {
  dayGroup: DayGroup;
  monthGroup: MonthGroup;
  globalIndex: number;
}

// Pencil edit icon
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// Expand/fullscreen icon
const ExpandIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);

// Close icon
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Trash icon for deleting events
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// Plus icon for adding events
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// History/clock icon for version history
const HistoryIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// Fullscreen icon for timeline toggle
const FullscreenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

// Minimize icon for exiting fullscreen
const MinimizeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 14 10 14 10 20" />
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

export function TimelineView({ rows, onEditClick, onDeleteClick, onCreateClick, onHistoryClick, pendingChanges = {} }: TimelineViewProps) {
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [fullscreenEvent, setFullscreenEvent] = useState<any | null>(null);
  const [highlightedDate, setHighlightedDate] = useState<string | null>(null);
  const [isTimelineFullscreen, setIsTimelineFullscreen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToToday = useRef(false);

  // Track which events are showing pending view vs current view
  const [showPendingFor, setShowPendingFor] = useState<Set<string>>(new Set());

  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });
  const velocityRef = useRef(0);
  const lastMoveRef = useRef({ x: 0, time: 0 });
  const momentumAnimationRef = useRef<number | null>(null);

  // Handle escape key to close fullscreen modal or timeline fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenEvent) {
          setFullscreenEvent(null);
        } else if (isTimelineFullscreen) {
          setIsTimelineFullscreen(false);
        } else if (expandedEvent) {
          setExpandedEvent(null);
          setHighlightedDate(null);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenEvent, expandedEvent, isTimelineFullscreen]);

  // Parse events and sort by date (oldest to newest for left-to-right display)
  const sortedEvents = useMemo(() => {
    return rows
      .map((row) => {
        const startDate = row.event_start_date ? new Date(row.event_start_date) : null;
        const endDate = row.event_end_date ? new Date(row.event_end_date) : null;
        const fallbackDate = row.created_at ? new Date(row.created_at) : new Date();
        return {
          row,
          date: startDate || fallbackDate,
          endDate,
          hasRealDate: !!startDate,
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [rows]);

  // Generate all days between first and last event, grouped by month
  const monthGroups = useMemo((): MonthGroup[] => {
    if (sortedEvents.length === 0) return [];

    // Find date range
    const firstDate = new Date(sortedEvents[0].date);
    const lastDate = new Date(sortedEvents[sortedEvents.length - 1].date);

    // Normalize to start of day
    firstDate.setHours(0, 0, 0, 0);
    lastDate.setHours(0, 0, 0, 0);

    // Create a map of events by date key for quick lookup
    const eventsByDate = new Map<string, TimelineEvent[]>();
    sortedEvents.forEach((event) => {
      const dateKey = `${event.date.getFullYear()}-${event.date.getMonth()}-${event.date.getDate()}`;
      if (!eventsByDate.has(dateKey)) {
        eventsByDate.set(dateKey, []);
      }
      eventsByDate.get(dateKey)!.push(event);
    });

    // Generate all days in range
    const groups: Map<string, MonthGroup> = new Map();
    const currentDate = new Date(firstDate);

    while (currentDate <= lastDate) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const day = currentDate.getDate();
      const monthKey = `${year}-${month}`;
      const dateKey = `${year}-${month}-${day}`;

      if (!groups.has(monthKey)) {
        groups.set(monthKey, { year, month, days: [] });
      }

      const monthGroup = groups.get(monthKey)!;
      const eventsForDay = eventsByDate.get(dateKey) || [];

      monthGroup.days.push({
        date: new Date(year, month, day),
        events: eventsForDay,
      });

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Sort months chronologically (oldest first for left-to-right)
    return Array.from(groups.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  }, [sortedEvents]);

  // Flatten all day columns for virtualization
  const flatDayColumns = useMemo((): FlatDayColumn[] => {
    const columns: FlatDayColumn[] = [];
    let globalIndex = 0;
    monthGroups.forEach((monthGroup) => {
      monthGroup.days.forEach((dayGroup) => {
        columns.push({ dayGroup, monthGroup, globalIndex: globalIndex++ });
      });
    });
    return columns;
  }, [monthGroups]);

  // Column width constant for virtualization
  const COLUMN_WIDTH = 100;

  // Initialize virtualizer for horizontal scrolling
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: flatDayColumns.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => COLUMN_WIDTH,
    overscan: 5, // Render extra columns for smooth momentum scrolling
  });

  // Calculate visible month ranges from virtualized columns
  const visibleMonthRanges = useMemo(() => {
    const virtualItems = columnVirtualizer.getVirtualItems();
    if (virtualItems.length === 0) return [];

    const monthMap = new Map<string, { startPx: number; endPx: number; monthGroup: MonthGroup }>();

    virtualItems.forEach((item) => {
      const col = flatDayColumns[item.index];
      if (!col) return;
      const key = `${col.monthGroup.year}-${col.monthGroup.month}`;

      if (!monthMap.has(key)) {
        monthMap.set(key, {
          startPx: item.start,
          endPx: item.start + item.size,
          monthGroup: col.monthGroup,
        });
      } else {
        const existing = monthMap.get(key)!;
        existing.endPx = item.start + item.size;
      }
    });

    return Array.from(monthMap.values());
  }, [columnVirtualizer.getVirtualItems(), flatDayColumns]);

  // Scroll to center on today's date on initial mount only and highlight it
  useEffect(() => {
    if (hasScrolledToToday.current) return; // Only scroll once
    if (flatDayColumns.length === 0) return;

    hasScrolledToToday.current = true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find today's index in the flattened columns
    let todayIndex = -1;
    let todayDateKey = '';

    for (let i = 0; i < flatDayColumns.length; i++) {
      const col = flatDayColumns[i];
      const dayDate = new Date(col.dayGroup.date);
      dayDate.setHours(0, 0, 0, 0);

      if (dayDate.getTime() === today.getTime()) {
        todayIndex = i;
        todayDateKey = `${col.dayGroup.date.getFullYear()}-${col.dayGroup.date.getMonth()}-${col.dayGroup.date.getDate()}`;
        break;
      }
      if (dayDate.getTime() > today.getTime()) {
        // Today is before this date, use previous position
        todayIndex = Math.max(0, i - 1);
        break;
      }
    }

    // Highlight today's date
    if (todayDateKey) {
      setHighlightedDate(todayDateKey);
    }

    // Scroll to position
    if (todayIndex >= 0) {
      columnVirtualizer.scrollToIndex(todayIndex, { align: 'center' });
    } else {
      // If today is not in range, scroll to the end (most recent)
      columnVirtualizer.scrollToIndex(flatDayColumns.length - 1, { align: 'end' });
    }
  }, [flatDayColumns, columnVirtualizer]);

  // Momentum animation function
  const applyMomentum = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const friction = 0.95; // Deceleration factor (lower = more friction)
    const minVelocity = 0.5; // Stop when velocity is below this

    if (Math.abs(velocityRef.current) > minVelocity) {
      container.scrollLeft += velocityRef.current;
      velocityRef.current *= friction;
      momentumAnimationRef.current = requestAnimationFrame(applyMomentum);
    } else {
      velocityRef.current = 0;
      momentumAnimationRef.current = null;
    }
  };

  // Drag-to-scroll handlers
  const handleContainerMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Stop any ongoing momentum animation
    if (momentumAnimationRef.current) {
      cancelAnimationFrame(momentumAnimationRef.current);
      momentumAnimationRef.current = null;
    }

    setIsDragging(true);
    setDragStart({ x: e.clientX, scrollLeft: container.scrollLeft });
    lastMoveRef.current = { x: e.clientX, time: Date.now() };
    velocityRef.current = 0;
  };

  const handleContainerMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    e.preventDefault();

    const now = Date.now();
    const dx = e.clientX - dragStart.x;
    container.scrollLeft = dragStart.scrollLeft - dx;

    // Calculate velocity based on recent movement
    const timeDelta = now - lastMoveRef.current.time;
    if (timeDelta > 0) {
      const moveDelta = lastMoveRef.current.x - e.clientX;
      velocityRef.current = (moveDelta / timeDelta) * 16; // Scale to ~60fps
    }
    lastMoveRef.current = { x: e.clientX, time: now };
  };

  const handleContainerMouseUp = () => {
    setIsDragging(false);
    // Apply momentum if there's velocity
    if (Math.abs(velocityRef.current) > 0.5) {
      momentumAnimationRef.current = requestAnimationFrame(applyMomentum);
    }
  };

  const handleContainerMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      // Apply momentum when leaving while dragging
      if (Math.abs(velocityRef.current) > 0.5) {
        momentumAnimationRef.current = requestAnimationFrame(applyMomentum);
      }
    }
  };

  if (rows.length === 0) {
    return (
      <div className="timeline-empty">
        <p className="text-muted">No events to display</p>
      </div>
    );
  }

  const handleEventClick = (row: any, dateKey: string) => {
    const rowId = getRowId(row);
    if (expandedEvent === rowId) {
      setExpandedEvent(null);
      setHighlightedDate(null);
    } else {
      setExpandedEvent(rowId);
      setHighlightedDate(dateKey);
    }
  };

  const handleEditClick = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(row);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    if (onDeleteClick) {
      onDeleteClick(row);
    }
  };

  const handleExpandClick = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    setFullscreenEvent(row);
  };

  const handleHistoryClick = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    if (onHistoryClick) {
      onHistoryClick(row);
    }
  };

  const togglePendingView = (rowId: string, showPending: boolean) => {
    setShowPendingFor(prev => {
      const next = new Set(prev);
      if (showPending) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  };

  // Helper to get the display row (either current or with pending changes applied)
  const getDisplayRow = (row: any): any => {
    const rowId = getRowId(row);
    const pendingInfo = pendingChanges[rowId];
    if (pendingInfo && showPendingFor.has(rowId)) {
      return applyPendingChanges(row, pendingInfo.changes);
    }
    return row;
  };

  // Get pending info for fullscreen event
  const fullscreenRowId = fullscreenEvent ? getRowId(fullscreenEvent) : null;
  const fullscreenPendingInfo = fullscreenRowId ? pendingChanges[fullscreenRowId] : undefined;
  const isFullscreenShowingPending = fullscreenRowId ? showPendingFor.has(fullscreenRowId) : false;
  const fullscreenDisplayRow = fullscreenEvent ? getDisplayRow(fullscreenEvent) : null;

  return (
    <>
    {/* Fullscreen modal */}
    {fullscreenEvent && fullscreenDisplayRow && (
      <div className="modal-overlay" onClick={() => setFullscreenEvent(null)}>
        <div className="modal modal-fullscreen-event" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: 0, marginBottom: 'var(--spacing-sm)' }}>
                {fullscreenDisplayRow.title || 'Untitled Event'}
              </h3>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                {fullscreenPendingInfo && (
                  <span className="badge badge-pending">Pending Changes</span>
                )}
                {fullscreenDisplayRow.procedure_type && (
                  <span className="badge badge-secondary">{fullscreenDisplayRow.procedure_type}</span>
                )}
                {fullscreenDisplayRow.status_sample && (
                  <span className="badge">{fullscreenDisplayRow.status_sample}</span>
                )}
                {fullscreenDisplayRow.priority && (
                  <span className="badge badge-primary">{fullscreenDisplayRow.priority}</span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              {onHistoryClick && (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setFullscreenEvent(null);
                    onHistoryClick(fullscreenEvent);
                  }}
                  style={{ padding: 'var(--spacing-sm)' }}
                >
                  <HistoryIcon /> History
                </button>
              )}
              {onEditClick && (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setFullscreenEvent(null);
                    onEditClick(fullscreenEvent);
                  }}
                  style={{ padding: 'var(--spacing-sm)' }}
                >
                  <PencilIcon /> Edit
                </button>
              )}
              {onDeleteClick && (
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    setFullscreenEvent(null);
                    onDeleteClick(fullscreenEvent);
                  }}
                  style={{ padding: 'var(--spacing-sm)' }}
                >
                  <TrashIcon /> Delete
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => setFullscreenEvent(null)}
                style={{ padding: 'var(--spacing-sm)' }}
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Inline review for vendor_proposed changes in fullscreen */}
          {fullscreenEvent?.vendor_proposed && Object.keys(fullscreenEvent.vendor_proposed).length > 0 && fullscreenRowId && (
            <div className="fullscreen-pending-section">
              <PendingChangeReview
                sampleId={fullscreenRowId}
                vendorProposed={fullscreenEvent.vendor_proposed as Record<string, unknown>}
                vendorSource={fullscreenEvent.vendor_proposed_source}
                vendorProposedAt={fullscreenEvent.vendor_proposed_at}
                onReviewComplete={() => {
                  setFullscreenEvent(null);
                  window.dispatchEvent(new CustomEvent('pendingChangesUpdated'));
                }}
                compact={false}
                rowData={fullscreenEvent}
              />
            </div>
          )}

          {/* Legacy pending changes section in fullscreen modal */}
          {fullscreenPendingInfo && fullscreenRowId && !fullscreenEvent?.vendor_proposed && (
            <div className="fullscreen-pending-section">
              <PendingChangeToggle
                showPending={isFullscreenShowingPending}
                onToggle={(show) => togglePendingView(fullscreenRowId, show)}
                pendingInfo={fullscreenPendingInfo}
              />
              {isFullscreenShowingPending && (
                <PendingChangeDiff
                  changes={fullscreenPendingInfo.changes}
                  fieldLabels={FIELD_DISPLAY_NAMES}
                  rowData={fullscreenEvent}
                />
              )}
            </div>
          )}

          <div style={{ marginTop: 'var(--spacing-lg)' }}>
            {renderFullEventDetails(fullscreenDisplayRow)}
          </div>
        </div>
      </div>
    )}
    <div className={`horizontal-timeline-wrapper ${isTimelineFullscreen ? 'horizontal-timeline-fullscreen' : ''}`}>
      <button
        className="horizontal-timeline-fullscreen-btn"
        onClick={() => setIsTimelineFullscreen(!isTimelineFullscreen)}
        title={isTimelineFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isTimelineFullscreen ? <MinimizeIcon /> : <FullscreenIcon />}
      </button>
      <div
        className={`horizontal-timeline-container ${isDragging ? 'dragging' : ''}`}
        ref={scrollContainerRef}
        onMouseDown={handleContainerMouseDown}
        onMouseMove={handleContainerMouseMove}
        onMouseUp={handleContainerMouseUp}
        onMouseLeave={handleContainerMouseLeave}
      >
        {/* Month labels row - virtualized */}
        <div
          className="horizontal-timeline-month-row"
          style={{ width: columnVirtualizer.getTotalSize(), position: 'relative' }}
        >
          {visibleMonthRanges.map(({ startPx, endPx, monthGroup }) => {
            const monthKey = `${monthGroup.year}-${monthGroup.month}`;
            const monthName = new Date(monthGroup.year, monthGroup.month).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            });
            return (
              <div
                key={`month-${monthKey}`}
                className="horizontal-timeline-month-label"
                style={{
                  position: 'absolute',
                  left: startPx,
                  width: endPx - startPx,
                }}
              >
                {monthName}
              </div>
            );
          })}
        </div>

        {/* Day columns - virtualized */}
        <div
          className="horizontal-timeline-columns"
          style={{
            width: columnVirtualizer.getTotalSize(),
            position: 'relative',
          }}
        >
          {columnVirtualizer.getVirtualItems().map((virtualItem) => {
            const col = flatDayColumns[virtualItem.index];
            if (!col) return null;
            const { dayGroup } = col;
            const dateKey = `${dayGroup.date.getFullYear()}-${dayGroup.date.getMonth()}-${dayGroup.date.getDate()}`;
            const isHighlighted = highlightedDate === dateKey;

            return (
              <div
                key={dateKey}
                className={`horizontal-timeline-column ${isHighlighted ? 'horizontal-timeline-column-highlighted' : ''}`}
                style={{
                  position: 'absolute',
                  left: virtualItem.start,
                  width: virtualItem.size,
                }}
              >
                {/* Day header */}
                <div className="horizontal-timeline-day-header">
                  <span className="horizontal-timeline-day-number">
                    {dayGroup.date.getDate()}
                  </span>
                  <span className="horizontal-timeline-day-name">
                    {dayGroup.date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                </div>

                {/* Track line segment */}
                <div className="horizontal-timeline-track-segment">
                  <div className="horizontal-timeline-track-line" />
                </div>

                {/* Events for this day */}
                <div className="horizontal-timeline-column-events">
                  {dayGroup.events.map((event, idx) => {
                    const rowId = getRowId(event.row);
                    const isExpanded = expandedEvent === rowId;
                    const pendingInfo = pendingChanges[rowId];
                    const hasPending = !!pendingInfo;
                    const isShowingPending = showPendingFor.has(rowId);
                    const displayRow = getDisplayRow(event.row);

                    return (
                      <div
                        key={`${rowId}-${idx}`}
                        className={`horizontal-timeline-event ${isExpanded ? 'horizontal-timeline-event-expanded' : ''} ${!event.hasRealDate ? 'horizontal-timeline-event-no-date' : ''} ${hasPending ? 'horizontal-timeline-event-has-pending' : ''}`}
                        onClick={() => handleEventClick(event.row, dateKey)}
                      >
                        <div className="horizontal-timeline-event-dot-container">
                          <div
                            className="horizontal-timeline-event-dot"
                            style={{ backgroundColor: event.hasRealDate ? getEventColor(displayRow) : '#ef4444' }}
                          />
                          {hasPending && <PendingDot />}
                        </div>
                        <div className={`horizontal-timeline-event-card ${!event.hasRealDate ? 'horizontal-timeline-event-card-no-date' : ''} ${isShowingPending ? 'horizontal-timeline-event-card-pending' : ''}`}>
                          {isExpanded && (
                            <div className="horizontal-timeline-event-actions">
                              <button
                                className="horizontal-timeline-action-btn"
                                onClick={(e) => handleExpandClick(e, event.row)}
                                title="View full details"
                              >
                                <ExpandIcon />
                              </button>
                              {onHistoryClick && (
                                <button
                                  className="horizontal-timeline-action-btn"
                                  onClick={(e) => handleHistoryClick(e, event.row)}
                                  title="View version history"
                                >
                                  <HistoryIcon />
                                </button>
                              )}
                              {onEditClick && (
                                <button
                                  className="horizontal-timeline-action-btn"
                                  onClick={(e) => handleEditClick(e, event.row)}
                                  title="Edit event"
                                >
                                  <PencilIcon />
                                </button>
                              )}
                              {onDeleteClick && (
                                <button
                                  className="horizontal-timeline-action-btn horizontal-timeline-action-btn-danger"
                                  onClick={(e) => handleDeleteClick(e, event.row)}
                                  title="Delete event"
                                >
                                  <TrashIcon />
                                </button>
                              )}
                            </div>
                          )}
                          <div className="horizontal-timeline-event-title">
                            {displayRow.title || 'Untitled'}
                          </div>
                          {!event.hasRealDate && (
                            <span className="badge badge-warning" style={{ fontSize: '10px' }}>
                              No Date
                            </span>
                          )}
                          {hasPending && (
                            <span className="badge badge-pending" style={{ fontSize: '10px' }}>
                              Pending
                            </span>
                          )}
                          {displayRow.procedure_type && (
                            <span className="badge badge-secondary" style={{ fontSize: '10px' }}>
                              {displayRow.procedure_type}
                            </span>
                          )}
                          {isExpanded && (
                            <div className="horizontal-timeline-event-details">
                              {/* Inline review for vendor_proposed changes */}
                              {event.row.vendor_proposed && Object.keys(event.row.vendor_proposed).length > 0 && (
                                <div className="horizontal-timeline-pending-section" onClick={(e) => e.stopPropagation()}>
                                  <PendingChangeReview
                                    sampleId={rowId}
                                    vendorProposed={event.row.vendor_proposed as Record<string, unknown>}
                                    vendorSource={event.row.vendor_proposed_source}
                                    vendorProposedAt={event.row.vendor_proposed_at}
                                    onReviewComplete={() => {
                                      // Trigger parent refresh - this will be handled by the parent component
                                      window.dispatchEvent(new CustomEvent('pendingChangesUpdated'));
                                    }}
                                    compact={true}
                                    rowData={event.row}
                                  />
                                </div>
                              )}
                              {/* Legacy pending changes display (for backward compatibility) */}
                              {hasPending && pendingInfo && !event.row.vendor_proposed && (
                                <div className="horizontal-timeline-pending-section" onClick={(e) => e.stopPropagation()}>
                                  <PendingChangeToggle
                                    showPending={isShowingPending}
                                    onToggle={(show) => togglePendingView(rowId, show)}
                                    pendingInfo={pendingInfo}
                                  />
                                  {isShowingPending && (
                                    <PendingChangeDiff
                                      changes={pendingInfo.changes}
                                      fieldLabels={FIELD_DISPLAY_NAMES}
                                      rowData={event.row}
                                    />
                                  )}
                                </div>
                              )}
                              {displayRow.status_sample && (
                                <div className="text-muted" style={{ fontSize: '11px' }}>
                                  Status: {displayRow.status_sample}
                                </div>
                              )}
                              {displayRow.subject && (
                                <div className="text-muted" style={{ fontSize: '11px' }}>
                                  Subject: {displayRow.subject}
                                </div>
                              )}
                              {renderEventDetails(displayRow)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add event button for this day */}
                  {onCreateClick && (
                    <button
                      className="horizontal-timeline-add-event-btn"
                      onClick={() => onCreateClick(dayGroup.date)}
                      title={`Add event on ${dayGroup.date.toLocaleDateString()}`}
                    >
                      <PlusIcon />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </>
  );
}

// Helper to render full event details in modal
function renderFullEventDetails(row: any): JSX.Element {
  // Get allowed fields for this procedure type
  const procedureType = row.procedure_type;
  const allowedDbFields = getFieldsForProcedureType(procedureType);

  // Map of view column names to their DB equivalents for filtering
  const viewToDbMapping: Record<string, string> = {
    'subject': 'subject_id',
    'banking_entity': 'banking_entity_id',
    'performing_entity': 'performing_entity_id',
    'location_performed': 'location_performed_id',
    'location_destination': 'location_destination_id',
    'transfer_origin': 'transfer_origin_id',
    'transfer_destination': 'transfer_destination_id',
    'source_sample_label': 'source_sample_id',
    'blocked_by': 'blocked_by_sample_id',
  };

  // All potential fields to show in fullscreen view (human-readable names from joined views)
  const allFields = [
    { key: 'subject', label: 'Subject' },
    { key: 'sample_type', label: 'Sample Type' },
    { key: 'sample_subtype', label: 'Sample Subtype' },
    { key: 'performed_on', label: 'Performed On' },
    { key: 'status_patient', label: 'Patient Status' },
    { key: 'event_start_date', label: 'Start Date' },
    { key: 'event_end_date', label: 'End Date' },
    { key: 'next_check_in_date', label: 'Next Check-in' },
    { key: 'analysis_complete', label: 'Analysis Complete' },
    { key: 'sample_received', label: 'Sample Received' },
    { key: 'transfer_involved', label: 'Transfer Involved' },
    { key: 'banking_entity', label: 'Banking Entity' },
    { key: 'performing_entity', label: 'Performing Entity' },
    { key: 'location_performed', label: 'Location Performed' },
    { key: 'location_destination', label: 'Location Destination' },
    { key: 'transfer_origin', label: 'Transfer Origin' },
    { key: 'transfer_destination', label: 'Transfer Destination' },
    { key: 'transfer_method', label: 'Transfer Method' },
    { key: 'derived_material', label: 'Derived Material' },
    { key: 'place', label: 'Place' },
    { key: 'source_sample_label', label: 'Source Sample' },
    { key: 'blocked_by', label: 'Blocked By' },
    { key: 'used_in_syt1_ideas', label: 'Used in SYT1 Ideas' },
    { key: 'syt1_ideas', label: 'SYT1 Ideas' },
    { key: 'outcome_notes', label: 'Outcome / Notes' },
    { key: 'priority', label: 'Priority' },
    { key: 'blocking', label: 'Blocking' },
    { key: 'is_active', label: 'Active?' },
    { key: 'structured_data', label: 'Extra Fields' },
  ];

  // Filter fields based on procedure type
  const filteredFields = allFields.filter(({ key }) => {
    // Map view column to DB column for checking
    const dbColumn = viewToDbMapping[key] || key;
    return allowedDbFields.includes(dbColumn);
  });

  const details = filteredFields
    .filter(({ key }) => row[key] !== null && row[key] !== undefined && row[key] !== '')
    .map(({ key, label }) => ({
      key,
      label,
      value: row[key],
    }));

  if (details.length === 0) {
    return <div className="text-muted">No additional details available</div>;
  }

  return (
    <div className="fullscreen-event-details">
      {details.map(({ key, label, value }) => (
        <div key={key} className="fullscreen-event-detail-row">
          <span className="fullscreen-event-detail-label">{label}</span>
          <span className="fullscreen-event-detail-value">{formatFieldValueFull(value, key)}</span>
        </div>
      ))}
    </div>
  );
}

// Helper to format field values (full, no truncation)
function formatFieldValueFull(value: any, fieldKey?: string): React.ReactNode {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  // Special handling for notes field (handles both string and parsed array)
  if (fieldKey === 'outcome_notes' && isNotesJson(value)) {
    return formatNotesForDisplay(value);
  }

  // Special handling for structured_data field
  if (fieldKey === 'structured_data' && typeof value === 'string') {
    return formatStructuredDataForDisplay(value);
  }

  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

// Helper to get event color based on procedure type or status
function getEventColor(row: any): string {
  const procedureType = row.procedure_type?.toLowerCase() || '';
  const status = row.status_sample?.toLowerCase() || '';

  // Color by procedure type
  if (procedureType.includes('transfer') || procedureType.includes('shipping')) {
    return '#3b82f6'; // blue
  }
  if (procedureType.includes('sampling')) {
    return '#10b981'; // green
  }
  if (procedureType.includes('eeg')) {
    return '#8b5cf6'; // purple
  }
  if (procedureType.includes('analysis')) {
    return '#f59e0b'; // amber
  }

  // Color by status if no procedure type match
  if (status.includes('complete')) {
    return '#10b981'; // green
  }
  if (status.includes('pending') || status.includes('progress')) {
    return '#f59e0b'; // amber
  }
  if (status.includes('failed') || status.includes('cancelled')) {
    return '#ef4444'; // red
  }

  // Default color
  return '#6b7280'; // gray
}

// Helper to get unique row ID
function getRowId(row: any): string {
  return row.sample_id || row.id || JSON.stringify(row);
}

// Helper to render expanded event details (compact version)
function renderEventDetails(row: any): JSX.Element {
  // Get allowed fields for this procedure type
  const procedureType = row.procedure_type;
  const allowedDbFields = getFieldsForProcedureType(procedureType);

  // Map of view column names to their DB equivalents for filtering
  const viewToDbMapping: Record<string, string> = {
    'performing_entity': 'performing_entity_id',
    'location_performed': 'location_performed_id',
  };

  // Key fields to show in compact expanded view (human-readable names from joined views)
  const detailFields = [
    { key: 'sample_type', label: 'Type' },
    { key: 'performing_entity', label: 'Performer' },
    { key: 'location_performed', label: 'Location' },
    { key: 'outcome_notes', label: 'Notes' },
  ];

  // Filter fields based on procedure type
  const filteredFields = detailFields.filter(({ key }) => {
    const dbColumn = viewToDbMapping[key] || key;
    return allowedDbFields.includes(dbColumn);
  });

  const details = filteredFields
    .filter(({ key }) => row[key] !== null && row[key] !== undefined && row[key] !== '')
    .map(({ key, label }) => ({
      key,
      label,
      value: row[key],
    }));

  if (details.length === 0) {
    return <></>;
  }

  return (
    <div className="timeline-event-details-grid">
      {details.map(({ key, label, value }) => (
        <div key={key} className="timeline-event-detail-row">
          <span className="timeline-event-detail-label">{label}:</span>
          <span className="timeline-event-detail-value">{formatFieldValueCompact(value, key)}</span>
        </div>
      ))}
    </div>
  );
}

// Helper to format field values (compact version with special handling for notes)
function formatFieldValueCompact(value: any, fieldKey?: string): React.ReactNode {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  // Special handling for notes field - use nice UI formatting (handles both string and parsed array)
  if (fieldKey === 'outcome_notes' && isNotesJson(value)) {
    return formatNotesForDisplay(value);
  }

  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' && value.length > 100) {
    return value.substring(0, 100) + '...';
  }
  return String(value);
}
